import React, { useState } from "react";
import { Box, Avatar, Typography, Chip } from "@mui/material";
import {
  DataGrid,
  GridToolbarQuickFilter,
  GridColDef,
  GridPaginationModel,
} from "@mui/x-data-grid";
import { createTheme, ThemeProvider } from "@mui/material/styles";
// import CommentTextField from "./CommentTextField"; // Import the CommentTextField component
import CommentTextField from "../genric_comp/commentTextField"

// Sample Data for Shifts
const shiftRows = Array.from({ length: 50 }, (_, index) => ({
  id: index + 1,
  employee: `Employee ${index + 1}`,
  avatar: `https://i.pravatar.cc/150?img=${index + 1}`,
  shiftDate: new Date(
    2024,
    Math.floor(Math.random() * 12),
    Math.floor(Math.random() * 28)
  ).toLocaleDateString(),
  status: ["Completed", "Rejected"][index % 2],
  hoursWorked: (Math.random() * 8).toFixed(1), // Random hours between 0 and 8
  comment: "", // Initial empty comment
}));

// DataGrid Columns
const shiftColumns: GridColDef[] = [
  {
    field: "avatar",
    headerName: "",
    width: 80,
    renderCell: (params) => (
      <Avatar src={params.value} alt="Avatar" sx={{ width: 40, height: 40 }} />
    ),
    sortable: false,
    filterable: false,
  },
  { field: "id", headerName: "ID", flex: 0.5 },
  { field: "employee", headerName: "Employee Name", flex: 1 },
  { field: "shiftDate", headerName: "Shift Date", flex: 1 },
  {
    field: "status",
    headerName: "Status",
    flex: 1,
    renderCell: (params) => {
      const statusColors: { [key: string]: "success" | "error" } = {
        Completed: "success",
        Rejected: "error",
      };
      return (
        <Chip
          label={params.value}
          variant="outlined"
          color={statusColors[params.value as keyof typeof statusColors]}
          sx={{
            fontSize: "13px",
            textTransform: "uppercase",
            borderWidth: "2px",
            borderRadius: "8px",
            padding: "2px 6px",
            fontWeight: "normal",
          }}
        />
      );
    },
  },
  { field: "hoursWorked", headerName: "Hours Worked", flex: 0.5 },
  {
    field: "comment",
    headerName: "Comments",
    flex: 2,
    renderCell: (params) => {
      return (
        <CommentTextField
          value={params.value || ""}
          onChange={(value: any) => params.api.updateRows([{ ...params.row, comment: value }])}
          label=""
        />
      );
    },
  },
];

// Custom Toolbar with Quick Filter
function CustomToolbar() {
  return (
    <Box
      sx={{
        p: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
        bgcolor: "background.paper",
        borderBottom: "1px solid #e0e0e0",
      }}
    >
      <Typography variant="h6" sx={{ fontWeight: "bold", ml: 2 }}>
        Shift Details
      </Typography>
      <GridToolbarQuickFilter
        placeholder="Search shifts..."
        debounceMs={500}
        quickFilterParser={(value) => value.split(" ")}
        sx={{
          width: "100%",
          maxWidth: 300,
          "& .MuiInputBase-root": {
            borderRadius: "5px",
            paddingLeft: "15px",
            bgcolor: "#f4f4f4",
            border: "1px solid #d6d6d6",
          },
        }}
      />
    </Box>
  );
}

const ShiftDetailsTable: React.FC = () => {
  // Pagination State
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });

  return (
    <ThemeProvider
      theme={createTheme({
        palette: {
          primary: { main: "#5c6bc0", contrastText: "#ffffff" },
          secondary: { main: "#90caf9" },
          background: { default: "#f5f5f5", paper: "#ffffff" },
        },
        typography: { fontFamily: "Roboto, Arial" },
      })}
    >
      <Box
        sx={{
          height: "100vh",
          width: "100%",
          bgcolor: "background.default",
          borderRadius: 3,
          boxShadow: 2,
          overflowX: "auto",
        }}
      >
        <DataGrid
          rows={shiftRows}
          columns={shiftColumns}
          components={{ Toolbar: CustomToolbar }}
          checkboxSelection
          pagination
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          sx={{
            minWidth: "600px",
            "& .MuiDataGrid-columnHeaders": {
              bgcolor: "#f4f4f4",
              color: "#333",
              fontSize: "15px",
              fontWeight: "bold",
            },
            "& .MuiDataGrid-row:nth-of-type(odd)": { bgcolor: "#fafafa" },
            "& .MuiDataGrid-row:nth-of-type(even)": { bgcolor: "#ffffff" },
            "& .MuiDataGrid-row:hover": { bgcolor: "#e3f2fd" },
            "& .MuiDataGrid-cell": { fontSize: "14px" },
            "& .MuiDataGrid-footerContainer": {
              bgcolor: "#f4f4f4",
              borderTop: "1px solid #e0e0e0",
            },
          }}
        />
      </Box>
    </ThemeProvider>
  );
};

export default ShiftDetailsTable;
